import { useParams, useNavigate } from 'react-router';
import { Text, Spinner, makeStyles, tokens, Button, Badge } from '@fluentui/react-components';
import { useQuery } from '@tanstack/react-query';
import { ChevronLeftRegular } from '@fluentui/react-icons';
import { fetchModules } from '../api/modules.api';
import { fetchDroneFlights } from '../api/drone-flights.api';
import { fetchAnomalies, fetchProductionPlan } from '../api/production.api';
import { fetchLotes } from '../api/lotes.api';
import { fetchCrops, fetchModuleCropHistory } from '../api/terrain.api';
import { ModuleHistoryPanel } from '../components/shared/ModuleHistoryPanel';
import { KpiCard } from '../components/shared/KpiCard';
import { GanttView, type GanttRow } from '../components/shared/GanttView';
import { SoilSensorChart } from '../components/shared/SoilSensorChart';
import { DroneVideoCard } from '../components/shared/DroneVideoCard';
import { useT } from '../i18n';
import { useLocaleSettings } from '../hooks/useLocaleSettings';

/**
 * F6 — Detalle de módulo con sensores de campo, vídeo de dron y anomalías.
 *
 * Pantalla 2 del PDF del cliente: muestra KPIs, histórico de fenología
 * (plan-vs-real de F2), gráficos de sensores de suelo (F5/F6), galería de
 * scouting y video de dron.
 *
 * Dependencias por fase — todas completas (Fase B, 2026-08-12 cerró las que
 * quedaban a medio camino: Fenología y Sensores de suelo eran placeholders
 * de texto/stub pese a que F2 y F5 ya estaban terminados):
 * - F1: módulos, válvulas asignadas ✓
 * - F2: ProductionPlan (plan-vs-real) ✓
 * - F5: dispositivos, sensor readings ✓
 * - F6: vuelos de dron, reglas de sensor ✓
 *
 * Las secciones solo se renderizan si hay datos + su config está activa
 * (D1 del spec: "nunca pintar gráfica vacía sugiriendo que hay datos").
 */

const useStyles = makeStyles({
  root: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL, padding: tokens.spacingVerticalL },
  header: { display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalM },
  backBtn: { alignSelf: 'flex-start' },
  heading: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  headingTitle: { margin: 0, fontSize: tokens.fontSizeBase600 },
  headingSubtitle: { margin: 0 },
  kpiRow: { display: 'flex', gap: tokens.spacingHorizontalM, flexWrap: 'wrap' },
  section: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  sectionTitle: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase400 },
  sectionContent: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalM },
  anomalyList: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalS },
  breadcrumb: { display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: tokens.spacingHorizontalXS },
  childList: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  anomalyItem: {
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  anomalySeverity: { fontWeight: tokens.fontWeightSemibold, fontSize: tokens.fontSizeBase200 },
});

export function InvestorModuleDetailPage() {
  const s = useStyles();
  const t = useT();
  const { formatDate } = useLocaleSettings();
  const { tenantId, moduleId } = useParams<{ tenantId: string; moduleId: string }>();
  const navigate = useNavigate();

  if (!tenantId || !moduleId) {
    return <Text>{t.invalidRequest}</Text>;
  }

  const { data: modules = [], isLoading: modulesLoading } = useQuery({
    queryKey: ['investor-modules'],
    queryFn: async () => fetchModules(),
  });

  const module = modules.find(m => m.moduleId === moduleId);

  const { data: lotes = [] } = useQuery({
    queryKey: ['lotes-breadcrumb'],
    queryFn: async () => fetchLotes(),
  });

  const { data: cropHistory = [] } = useQuery({
    queryKey: ['module-crop-history', moduleId],
    queryFn: async () => fetchModuleCropHistory(moduleId),
  });

  const { data: crops = [] } = useQuery({
    queryKey: ['crops'],
    queryFn: async () => fetchCrops(),
  });

  // Cadena de ancestros (zona → padre) para el breadcrumb; un solo nivel de
  // anidación, pero se recorre con tope por seguridad ante datos inconsistentes.
  const ancestors: typeof modules = [];
  let parentId = module?.parentModuleId;
  while (parentId && ancestors.length < 5) {
    const parent = modules.find(m => m.moduleId === parentId);
    if (!parent) break;
    ancestors.unshift(parent);
    parentId = parent.parentModuleId;
  }

  const children = modules.filter(m => m.parentModuleId === moduleId);
  const lote = lotes.find(l => l.loteId === module?.loteId);
  const cropNames = Object.fromEntries(crops.map(c => [c.cropId, c.name]));

  const { data: flights = [], isLoading: flightsLoading } = useQuery({
    queryKey: ['module-drone-flights', moduleId],
    queryFn: async () => fetchDroneFlights({ moduleId }),
  });

  const { data: anomalies = [], isLoading: anomaliesLoading } = useQuery({
    queryKey: ['module-anomalies', moduleId],
    queryFn: async () => {
      const all = await fetchAnomalies();
      return all.filter(a => a.moduleId === moduleId);
    },
  });

  // Plan de producción — se llenará cuando F2 esté lista
  const { data: productionPlanData = [], isLoading: planLoading } = useQuery({
    queryKey: ['module-production-plan', moduleId, module?.projectId],
    queryFn: async () => {
      if (!module?.projectId) return [];
      return fetchProductionPlan(module.projectId, moduleId);
    },
    enabled: !!module?.projectId,
  });

  if (modulesLoading) {
    return <Spinner size="large" label={t.common.loading} />;
  }

  if (!module) {
    return (
      <div className={s.root}>
        <Button icon={<ChevronLeftRegular />} onClick={() => navigate(-1)}>
          {t.investorModules.back}
        </Button>
        <Text>{t.investorModules.notFound}</Text>
      </div>
    );
  }

  const openAnomalies = anomalies.filter(a => ['ABIERTA', 'EN_PROCESO'].includes(a.status));

  const sortedPlan = [...productionPlanData].sort((a, b) => a.seq - b.seq);
  const ganttRows: GanttRow[] = sortedPlan.map(p => ({
    id: p.planKey,
    label: t.crop[`stage${p.stageCode}` as keyof typeof t.crop] as string,
    plannedStart: p.plannedStart,
    plannedEnd: p.plannedEnd,
    actualStart: p.actualStart,
    actualEnd: p.actualEnd,
    deviationDays: p.deviationDays,
  }));

  return (
    <div className={s.root}>
      <div className={s.header}>
        <Button
          appearance="subtle"
          icon={<ChevronLeftRegular />}
          className={s.backBtn}
          onClick={() => navigate(`/${tenantId}/fincas`)}
        />
        <div className={s.heading}>
          <nav aria-label="breadcrumb" className={s.breadcrumb}>
            <Button appearance="transparent" size="small" onClick={() => navigate(`/${tenantId}/campo/${module.loteId}`)}>
              {lote?.name ?? module.loteId}
            </Button>
            {ancestors.map(a => (
              <span key={a.moduleId} className={s.breadcrumb}>
                <Text size={200}>›</Text>
                <Button appearance="transparent" size="small" onClick={() => navigate(`/${tenantId}/fincas/modulo/${a.moduleId}`)}>
                  {a.moduleCode}
                </Button>
              </span>
            ))}
            <Text size={200}>› {module.moduleCode}</Text>
          </nav>
          <h2 className={s.headingTitle}>{module.moduleCode}</h2>
          <Text className={s.headingSubtitle} size={300}>
            {module.cropName || t.investorModules.noCrop} • {module.areaHa?.toFixed(1)} ha
          </Text>
        </div>
      </div>

      {/* Hijos (subzonas) — navegación de zona a subzona */}
      {children.length > 0 && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investorModules.childrenTitle}</span>
          <div className={s.childList}>
            {children.map(child => (
              <Button
                key={child.moduleId}
                appearance="outline"
                onClick={() => navigate(`/${tenantId}/fincas/modulo/${child.moduleId}`)}
              >
                {child.moduleCode}{child.name ? ` · ${child.name}` : ''}
              </Button>
            ))}
          </div>
        </div>
      )}

      {/* KPIs del módulo */}
      <div className={s.section}>
        <div className={s.kpiRow}>
          {module.status && (
            <div>
              <Text size={100} weight="semibold">{t.common.status}</Text>
              <Badge appearance="tint" style={{ marginTop: '4px' }}>
                {t.investorModules[`status${module.status}` as keyof typeof t.investorModules] || module.status}
              </Badge>
            </div>
          )}
          {module.plantingDate && (
            <div>
              <Text size={100} weight="semibold">{t.investorModules.plantingDate}</Text>
              <Text>{formatDate(module.plantingDate)}</Text>
            </div>
          )}
          {module.areaHa && (
            <KpiCard
              label={t.investorModules.area}
              value={`${module.areaHa.toFixed(2)} ha`}
            />
          )}
        </div>
      </div>

      {/* Fenología (plan-vs-real) — F2 */}
      {ganttRows.length > 0 && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investorModules.fenologyTitle}</span>
          <div className={s.sectionContent}>
            <GanttView
              rows={ganttRows}
              plannedLabel={t.investorProduction.ganttPlanned}
              actualLabel={t.investorProduction.ganttActual}
              onTimeLabel={t.investorProduction.ganttOnTime}
              lateLabel={t.investorProduction.ganttLate}
              earlyLabel={t.investorProduction.ganttEarly}
            />
          </div>
        </div>
      )}

      {/* Sensores de suelo — F5/F6 */}
      <div className={s.section}>
        <span className={s.sectionTitle}>{t.investorModules.soilSensorsTitle}</span>
        <div className={s.sectionContent}>
          <SoilSensorChart moduleId={moduleId} />
        </div>
      </div>

      {/* Video de dron — F6 */}
      <DroneVideoCard
        flights={flights}
        title={t.investorModules.videoTitle}
        loadingLabel={t.investorModules.loadingVideo}
        errorLabel={t.investorModules.videoLoadError}
      />

      {/* Anomalías abiertas */}
      {openAnomalies.length > 0 && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investorModules.anomaliesTitle}</span>
          <div className={s.anomalyList}>
            {openAnomalies.map(a => (
              <div key={a.anomalyId} className={s.anomalyItem}>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Text className={s.anomalySeverity}>{a.title}</Text>
                  <Badge appearance="tint" size="small">
                    {t.investorProduction[`severity${a.severity}` as keyof typeof t.investorProduction] ?? a.severity}
                  </Badge>
                </div>
                {a.description && <Text size={200}>{a.description}</Text>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Historial de cultivo de la zona — filtros por fecha, actividad y estado */}
      {(lote?.uso ?? 'CAMPO') === 'CAMPO' && (
        <div className={s.section}>
          <span className={s.sectionTitle}>{t.investorModules.historyTitle}</span>
          <ModuleHistoryPanel entries={cropHistory} cropNames={cropNames} />
        </div>
      )}

      {/* Loading states para secciones en carga */}
      {(planLoading || flightsLoading || anomaliesLoading) && (
        <Spinner size="small" label={t.common.loading} />
      )}
    </div>
  );
}
